import React, { useRef, useState } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { ImagePlus, Send, Loader2, X } from 'lucide-react';
import { api, fileUrl } from '@/lib/api';
import { toast } from 'sonner';
import EmojiPicker from '@/components/EmojiPicker';
import { useI18n } from '@/i18n';

/**
 * Reusable composer for comments / chats.
 * Props:
 *  - onSubmit({ text, image_path }) => Promise
 *  - placeholder
 *  - disabled
 *  - rows (default 2)
 *  - sendVariant: 'cyan' | 'pink' (button color)
 *  - testIdPrefix
 *  - autoFocus
 *  - sendOnEnter (boolean, default true for chats)
 */
export default function ChatComposer({
  onSubmit,
  placeholder,
  disabled,
  rows = 2,
  sendVariant = 'cyan',
  testIdPrefix = 'composer',
  autoFocus = false,
  sendOnEnter = false,
}) {
  const { t } = useI18n();
  const [text, setText] = useState('');
  const [imagePath, setImagePath] = useState(null);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const taRef = useRef(null);

  const insertAtCursor = (str) => {
    const el = taRef.current;
    if (!el) { setText((v) => v + str); return; }
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? text.length;
    const next = text.slice(0, start) + str + text.slice(end);
    setText(next);
    requestAnimationFrame(() => {
      el.focus();
      const p = start + str.length;
      try { el.setSelectionRange(p, p); } catch {}
    });
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error(t('composer.image_only'));
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error(t('composer.too_large'));
      return;
    }
    const fd = new FormData();
    fd.append('file', f);
    setUploading(true);
    try {
      const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImagePath(data.path);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const canSubmit = !disabled && !sending && !uploading && (text.trim() || imagePath);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSending(true);
    try {
      await onSubmit({ text: text.trim(), image_path: imagePath });
      setText('');
      setImagePath(null);
    } finally { setSending(false); }
  };

  const onKeyDown = (e) => {
    if (sendOnEnter && e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit(e);
    }
  };

  const sendColor = sendVariant === 'pink'
    ? 'bg-[#FF007F] hover:bg-[#FF007F]/90 text-white'
    : 'bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black';

  return (
    <div className="flex flex-col gap-2">
      {imagePath && (
        <div className="relative inline-block w-fit" data-testid={`${testIdPrefix}-image-preview`}>
          <img src={fileUrl(imagePath)} alt="" className="max-h-36 rounded-lg border border-white/10" />
          <button
            type="button"
            onClick={() => setImagePath(null)}
            data-testid={`${testIdPrefix}-image-remove`}
            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-black/80 border border-white/10 text-white grid place-items-center hover:bg-red-500/80"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      <form onSubmit={submit} className="flex items-end gap-2">
        <Textarea
          ref={taRef}
          data-testid={`${testIdPrefix}-input`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          rows={rows}
          disabled={disabled}
          autoFocus={autoFocus}
          className="bg-black/40 border-white/10 text-white placeholder:text-white/30 resize-none flex-1 min-h-[40px]"
        />
        <div className="flex items-center">
          <EmojiPicker onPick={insertAtCursor} testId={`${testIdPrefix}-emoji`} />
          <button
            type="button"
            data-testid={`${testIdPrefix}-image-btn`}
            disabled={disabled || uploading}
            onClick={() => fileRef.current?.click()}
            className="inline-grid place-items-center w-9 h-9 rounded-full text-white/60 hover:text-[#00F0FF] hover:bg-white/5 transition disabled:opacity-50"
            title={t('composer.add_image')}
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
          <Button
            type="submit"
            data-testid={`${testIdPrefix}-submit`}
            disabled={!canSubmit}
            className={`${sendColor} rounded-full ml-1 h-9 w-9 p-0`}
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </form>
    </div>
  );
}
